import React, { useCallback, useEffect, useState } from 'react';
import CopyButton from '../../shared/components/CopyButton';
import Pagination from '../../shared/components/Pagination';
import { extractErrorMessage } from '../../shared/utils/errors';
import { DepositAddressBatch, PaginatedResponse } from '../../shared/types';
import { formatNetworkLabel } from '../../shared/utils/networkLabel';
import { POOL_CURRENCIES } from '../../shared/utils/walletAddress';
import { PoolBatchFilters, listPoolBatches } from './api';
import { shortKid } from './PoolWalletsSection';

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

interface DepositBatchesSectionProps {
  refreshKey?: number;
}

/**
 * History of signed address imports. Each row keeps the original JWS so an
 * operator can re-check a batch against the signer's key at any time.
 */
const DepositBatchesSection: React.FC<DepositBatchesSectionProps> = ({ refreshKey = 0 }) => {
  const [batches, setBatches] = useState<PaginatedResponse<DepositAddressBatch> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<PoolBatchFilters>({});
  const [draftFilters, setDraftFilters] = useState<PoolBatchFilters>({});

  const load = useCallback(async (page: number, filtersToUse: PoolBatchFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      setBatches(await listPoolBatches({ ...filtersToUse, page, per_page: 20 }));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load import batches'));
      setBatches(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load, refreshKey]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  return (
    <>
      <div className="card p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
          <select
            value={draftFilters.currency ?? ''}
            onChange={(e) =>
              setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
            }
            className="input py-2"
          >
            <option value="">All</option>
            {POOL_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Signing key (kid)</label>
          <input
            type="text"
            value={draftFilters.kid ?? ''}
            onChange={(e) => setDraftFilters((p) => ({ ...p, kid: e.target.value || undefined }))}
            className="input py-2 font-mono text-xs"
            placeholder="Full kid"
          />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
            Apply
          </button>
          <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
            Clear
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : !batches?.data.length ? (
          <div className="p-12 text-center text-slate-400">No signed batches imported yet.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">ID</th>
                    <th className="table-header">Signer</th>
                    <th className="table-header">Pair</th>
                    <th className="table-header text-right">Addresses</th>
                    <th className="table-header text-right">Wallets</th>
                    <th className="table-header">Signed at</th>
                    <th className="table-header">Imported</th>
                    <th className="table-header">Status</th>
                    <th className="table-header text-right">JWS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {batches.data.map((batch) => (
                    <tr key={batch.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="table-cell font-mono text-xs text-slate-500">#{batch.id}</td>
                      <td className="table-cell text-xs">
                        <p className="text-slate-200">{batch.signer_label || '—'}</p>
                        <p className="font-mono text-slate-500" title={batch.kid}>
                          {shortKid(batch.kid)}
                        </p>
                      </td>
                      <td className="table-cell text-slate-300 text-sm whitespace-nowrap">
                        {batch.currency} · {formatNetworkLabel(batch.network)}
                      </td>
                      <td className="table-cell text-right font-mono text-slate-200">
                        {batch.address_count}
                      </td>
                      <td
                        className="table-cell text-right font-mono text-slate-300 whitespace-nowrap"
                        title={`${batch.active_wallets} active of ${batch.wallets} · ${batch.assigned_wallets} assigned to users`}
                      >
                        {batch.active_wallets}/{batch.wallets}
                        <span className="text-slate-500 ml-1">({batch.assigned_wallets} assigned)</span>
                      </td>
                      <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                        {formatDateTime(batch.iat)}
                      </td>
                      <td className="table-cell text-xs whitespace-nowrap">
                        <p className="text-slate-300">{formatDateTime(batch.created_at)}</p>
                        <p className="text-slate-500">{batch.imported_by || '—'}</p>
                      </td>
                      <td className="table-cell whitespace-nowrap">
                        {batch.signer_revoked ? (
                          <span className="badge badge-danger text-[10px]">Key revoked</span>
                        ) : !batch.signer_in_code ? (
                          <span className="badge badge-warning text-[10px]">Key removed</span>
                        ) : (
                          <span className="badge badge-success text-[10px]">Trusted</span>
                        )}
                      </td>
                      <td className="table-cell text-right">
                        <CopyButton value={batch.raw_jws} label="Copy JWS" small />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {batches.last_page > 1 && (
              <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500">
                  {batches.total} batch{batches.total === 1 ? '' : 'es'}
                </p>
                <Pagination
                  currentPage={batches.current_page}
                  lastPage={batches.last_page}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DepositBatchesSection;
